import { InputFieldWithLabel } from '../InputField';
import React, { useContext } from 'react';
import s from './Web5DeviceLogin.scss';
import { AppContextProvider } from '../AppLoader';
import { Box } from '@wix/design-system';
import { dataHooks } from '../../dataHooks';
import { DialogFormContainer } from '../DialogContainer';
import { keys } from '../../translationsKeys';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { ThemedHeading, ThemedButton, ThemedText } from '../ThemedComponents';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import { ROUTES } from '../../routes';

interface Web5DeviceLoginProps {}

export const Web5DeviceLogin: React.FC<Web5DeviceLoginProps> = observer(() => {
  const {
    rootStore: {
      web5DeviceLoginStore,
      navigationStore: { curryNavigate: navigate },
      displayStore: { isMobile },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const subtitleFontSize = 'medium';
  const emailFontSize = 'small';
  return (
    <Box direction="vertical" width="100%" align="center">
      <DialogFormContainer
        dataHook={dataHooks.web5DeviceLogin.container}
        formClasses={[s.web5DeviceLoginConnect]}
        mainHeaderProps={{ showBackButton: false }}
      >
        <ThemedHeading
          appearance={isMobile ? 'H2' : 'H1'}
          dataHook={dataHooks.web5DeviceLogin.title}
        >
          {t(keys['web5Device.login.connect.code.title'])}
        </ThemedHeading>
        <Box
          marginBottom="40px"
          marginTop={1}
          textAlign="center"
          display="grid"
          width="100%"
        >
          <ThemedText size={subtitleFontSize}>
            {t(keys['web5Device.login.connect.code.content.first'])}
          </ThemedText>
          <ThemedText size={subtitleFontSize}>
            {t(keys['web5Device.login.connect.code.content.second'])}
          </ThemedText>
        </Box>
        <Box direction="vertical" align="center" className={s.codeInput}>
          <InputFieldWithLabel
            required
            formField={web5DeviceLoginStore.code}
            label={t(keys['web5Device.login.connect.code.label'])}
            type="text"
            marginBottom={0}
            wrapperClassName={s.codeInput}
            dataHook={dataHooks.web5DeviceLogin.codeInput}
            autoFocus
            onChange={(e) => {
              web5DeviceLoginStore.code.setValue(e.target.value.toUpperCase());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                web5DeviceLoginStore.submit();
              }
            }}
          />
        </Box>
        <Box marginBottom={0} direction="vertical" verticalAlign="middle">
          <Trans
            i18nKey={web5DeviceLoginStore.contentKey}
            values={{
              currentLoggedInEmail: web5DeviceLoginStore?.currentLoggedInEmail,
            }}
          >
            <ThemedText size={emailFontSize}>
              You're currently logged in as
            </ThemedText>
            <ThemedText size={emailFontSize} weight="bold">
              email
            </ThemedText>
          </Trans>
        </Box>
        <Box verticalAlign="middle" paddingTop="36px">
          <Box
            gap="SP2"
            verticalAlign="middle"
            display={isMobile ? 'grid' : 'flex'}
            width="100%"
          >
            <ThemedButton
              size="large"
              onClick={() => {
                navigate(
                  `/?targetUrl=${window.location.origin}/login${ROUTES.WEB5_DEVICE_LOGIN}`,
                )();
              }}
              priority="secondary"
              skin="standard"
              dataHook={dataHooks.web5DeviceLogin.anotherAccountCta}
            >
              {t(keys['web5Device.login.connect.another.account.button'])}
            </ThemedButton>
            <ButtonWithLoader
              className={s.connectButton}
              disabled={!web5DeviceLoginStore.code.value.length}
              onClick={web5DeviceLoginStore.submit}
              dataHook={dataHooks.web5DeviceLogin.connect}
              skin="standard"
            >
              {t(keys['web5Device.login.connect.button'])}
            </ButtonWithLoader>
          </Box>
        </Box>
      </DialogFormContainer>
    </Box>
  );
});
